'use client';

/* Hallmark · genre: editorial · macrostructure: Narrative Workflow — R8 · theme: custom (warm ledger) · design-system: R8 contract */

import { Fragment } from 'react';
import { Inbox, Shuffle, ListChecks, UserRoundCheck, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLocale } from '../hooks/useLocale';
import { LANDING_CONTENT } from '../constants/content';
import { PAPER_BAND, PAPER_INK, PAPER_RULE, PAPER_ACCENT_INK } from '../ui/paper-tokens';
import LandingReveal from './LandingReveal';

/**
 * OneProcessSection — R8 restructure §P ("One process, from the ad to the
 * client."), spec of record `.claude/handoffs/2026-08-12-r8-restructure-
 * contract.md`. New section order slot #4 (Hero -> Differentiators ->
 * OneProcess -> BrainSystem -> ...); this file owns ONLY its own root band
 * paint + content, per the LandingPage.tsx header docblock's "each section
 * owns its own band paint" law. Band ladder proposal (contract "Band ladder"
 * section): `PAPER_BAND.base` — the integrator confirms/adjusts the final
 * ladder in LandingPage.tsx.
 *
 * Albert's explicit instruction (voice memo, quoted verbatim in the
 * contract's §P): NOT big bubble/card blocks — four SMALL elements (icon +
 * mono numeral), one short caption line UNDER each, thin connectors
 * repeating the 1->2->3->4 scheme. Quiet, low section height, no
 * boxes-in-boxes. Section padding (py-16/lg:py-20) and type scale below are
 * deliberately smaller than the heavier chapters (Differentiators/
 * BrainSystem use py-24/lg:py-32) to read as a quiet rail, not a fifth
 * headline chapter.
 *
 * CONNECTOR NOTE: the recon build-map lists `ui/Connector.tsx` as reusable
 * grammar for both §P and §B ("Connector — SVG line grammar — §P connectors
 * + §B hub"). That primitive hardcodes `bg-[#FFFFFF1A]` (a dark-band-only
 * hairline — its one real consumer, `ui/RuleFlow.tsx`, only ever composes
 * inside a dark `FigureFrame` exhibit interior). This section is plain page
 * chrome sitting directly on a `PAPER_BAND` — the system law confines dark
 * tokens to bounded exhibit frames, and painting `#FFFFFF1A` straight onto
 * paper would render a near-invisible, wrong-family hairline. The connector
 * below reuses Connector's GRAMMAR (a short line between two filled nodes)
 * with a paper-registry color instead — the same "local hairline on paper"
 * pattern `FeatureGridSection.tsx` already used for its own mobile chapter
 * divider (`border-t ${PAPER_RULE.hairline.border}`), not a new primitive.
 * No custom motion here on purpose — Albert asked for quieter, not more
 * motion; the section's only animation is the shared below-fold
 * `LandingReveal` entrance every other chapter already uses.
 */

const STEP_ICONS: readonly LucideIcon[] = [Inbox, Shuffle, ListChecks, UserRoundCheck];

function StepConnector() {
  return (
    <div
      aria-hidden="true"
      data-slot="connector"
      className={cn(
        'h-6 w-px shrink-0 border-l sm:h-px sm:w-8 sm:self-center sm:border-l-0 sm:border-t lg:w-12',
        PAPER_RULE.hairline.border
      )}
    />
  );
}

function OneProcessSectionInner() {
  const { t } = useLocale();
  const { oneProcess } = LANDING_CONTENT;
  const steps = oneProcess.steps;

  return (
    <section
      id="one-process"
      data-landing-chapter="one-process"
      className={cn('relative overflow-x-clip py-16 lg:py-20', PAPER_BAND.base.bg)}
    >
      <div className="relative z-10 mx-auto max-w-[1280px] px-5 sm:px-6 lg:px-8">
        <LandingReveal>
          <div className="mb-10 max-w-2xl lg:mb-12">
            <span
              className={cn(
                'mb-4 block text-[11px] font-semibold uppercase leading-4 tracking-[0.12em]',
                PAPER_INK.muted.text
              )}
            >
              {t(oneProcess.eyebrow)}
            </span>
            <h2
              className={cn(
                'landing-serif-display max-w-[18ch] text-[28px] leading-[1.15] lg:text-[34px] lg:leading-[1.1]',
                PAPER_INK.primary.text
              )}
            >
              {t(oneProcess.h2)}
            </h2>
          </div>

          <div data-slot="rail" className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-0">
            {steps.map((step, i) => {
              const Icon = STEP_ICONS[i] ?? STEP_ICONS[0];
              return (
                <Fragment key={step.num}>
                  <div
                    data-slot="step"
                    className="flex items-start gap-3 sm:flex-1 sm:flex-col sm:items-center sm:gap-0 sm:px-2 sm:text-center"
                  >
                    <div className="flex items-center gap-2 sm:flex-col sm:gap-1.5">
                      <span
                        aria-hidden="true"
                        className={cn(
                          'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border',
                          PAPER_RULE.hairline.border,
                          PAPER_BAND.alt.bg,
                          PAPER_INK.secondary.text
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                      </span>
                      <span
                        data-type-role="chapter-number"
                        className={cn(
                          'font-mono text-[11px] uppercase tabular-nums tracking-[0.14em]',
                          PAPER_ACCENT_INK.text
                        )}
                      >
                        {step.num}
                      </span>
                    </div>
                    <div className="min-w-0 sm:mt-2.5">
                      <h3
                        className={cn(
                          'text-[15px] font-medium leading-[1.3]',
                          PAPER_INK.primary.text
                        )}
                      >
                        {t(step.title)}
                      </h3>
                      <p
                        className={cn(
                          'mt-1 max-w-[26ch] text-[13px] leading-[1.5] sm:mx-auto',
                          PAPER_INK.muted.text
                        )}
                      >
                        {t(step.caption)}
                      </p>
                    </div>
                  </div>
                  {i < steps.length - 1 && <StepConnector />}
                </Fragment>
              );
            })}
          </div>
        </LandingReveal>
      </div>
    </section>
  );
}

export default function OneProcessSection() {
  return <OneProcessSectionInner />;
}
